import { Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import React, { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ComparisonCard from './BizviewComponents/ComparisonCard';

import PieChart from './BizviewComponents/PieChart';

import { useThemes } from '../Theme/theme';
import {
  useFetchBizviewDataQuery,
  useFetchListStockSummaryQuery,
  useFetchTotalClosingStockQuery,
} from '../../redux/apis/dashboardApi';
import AIBlinkButton from '../Buttons/AIBlinkButton';
import SimpleModal from '../modals/simpleModal';
import { axiosInstanceForLambda } from '../../library/Axios';
import { showWarningSnackbar } from '../snackBar/snakbar';
import API_ENDPOINTS from '../../apiEndpoints.js';
import BiZviewPeriodNew from './BizviewComponents/BiZviewPeriodNew.jsx';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getPreviousYearPeriod, determinePeriodType } from './BizviewComponents/utils/periodUtils';

const formatPeriodTitle = (period, listFYData = [], showFyString = true) => {
  const { startdate, enddate, financialyearid } = period;
  if (!startdate || !enddate) return '';

  const fyData = listFYData?.length > 0 ? listFYData?.find((fy) => fy.financialyearid === financialyearid) : null;

  if (!fyData) return '';

  const fyString = showFyString ? `FY ${fyData?.startyear.toString().slice(-2)} - ${fyData?.endyear.toString().slice(-2)}` : '';

  const start = moment(startdate * 1000);
  const end = moment(enddate * 1000);

  let dateString;
  if (start.isSame(end, 'day')) {
    dateString = ` (${start.format('MMM D')})`;
  } else {
    dateString = ` (${start.format('MMM D')} - ${end.format('MMM D')})`;
  }

  return fyString + dateString;
};

function BizView() {
  const { theme } = useThemes();
  const isUnder1200 = useMediaQuery('(max-width:1200px)');
  const [aiModal, setAIModal] = useState(false);
  const [aiModalLoading, setAIModalLoading] = useState(false);
  const [aisummary, setAISummary] = useState('');

  const [titles, setTitles] = useState({
    current: null,
    previous: null,
    previewCurrent: null,
    previousCurrent: null,
  });

  const [bizviewVariables, setBizviewVariables] = useState({
    startdate: null,
    enddate: null,
    openingbalance: 1,
    companyid: null,
    financialyearid: null,
  });

  const [bizviewVariables2, setBizviewVariables2] = useState({
    startdate: null,
    enddate: null,
    openingbalance: 1,
    companyid: null,
    financialyearid: null,
  });

  const areQueryVariableOneValid = useMemo(
    () => (bizviewVariables ? Object.values(bizviewVariables).every((val) => val) : false),
    [bizviewVariables]
  );
  const areQueryVariableTwoValid = useMemo(
    () => (bizviewVariables2 ? Object.values(bizviewVariables2).every((val) => val) : false),
    [bizviewVariables2]
  );

  const { data, isLoading: isLoadingData1, isFetching: isFetchingData1 } = useFetchBizviewDataQuery(bizviewVariables, {
    skip: !areQueryVariableOneValid,
    refetchOnMountOrArgChange: false,
  });

  const { data: totalClosingStock1 = [], isLoading: isLoadingClosingStock1, isFetching: isFetchingClosingStock1 } = useFetchTotalClosingStockQuery(
    {
      endtimestamp: bizviewVariables?.enddate,
      financialyearid: bizviewVariables?.financialyearid,
    },
    { skip: !areQueryVariableOneValid }
  );

  const { data: data2, isLoading: isLoadingData2, isFetching: isFetchingData2 } = useFetchBizviewDataQuery(bizviewVariables2, {
    skip: !areQueryVariableTwoValid,
    refetchOnMountOrArgChange: false,
  });

  const { data: totalClosingStock2 = [], isLoading: isLoadingClosingStock2, isFetching: isFetchingClosingStock2 } = useFetchTotalClosingStockQuery(
    {
      endtimestamp: bizviewVariables2?.enddate,
      financialyearid: bizviewVariables2?.financialyearid,
    },
    { skip: !areQueryVariableTwoValid }
  );

  const isCurrentDataLoading = useMemo(
    () => isLoadingData1 || isFetchingData1 || isLoadingClosingStock1 || isFetchingClosingStock1,
    [isLoadingData1, isFetchingData1, isLoadingClosingStock1, isFetchingClosingStock1]
  );

  const isPreviousDataLoading = useMemo(
    () => isLoadingData2 || isFetchingData2 || isLoadingClosingStock2 || isFetchingClosingStock2,
    [isLoadingData2, isFetchingData2, isLoadingClosingStock2, isFetchingClosingStock2]
  );

  const isComparisonCardLoading = useMemo(
    () => isCurrentDataLoading || isPreviousDataLoading,
    [isCurrentDataLoading, isPreviousDataLoading]
  );

  const filteredData = (cb) => {
    setAIModal(true);
    setAIModalLoading(true);
    const bizData =
      data?.map((item) => ({
        group_name: item.group_name,
        balance: item.balance,
      })) || [];
    cb(bizData);
  };

  const filterApiData = async (data) => {
    const payload = {
      tab_name: 'Business Overview',
      data,
    };
    try {
      const res = await axiosInstanceForLambda.post(API_ENDPOINTS.REACT_APP_AIBLINK_URL, payload);
      setAISummary(res.data.markdown_summary);
      setAIModalLoading(false);
    } catch (err) {
      showWarningSnackbar('Something went wrong please try again');
      setAIModalLoading(false);
    }
  };
  const { listFYData } = useSelector((state) => state?.userPrefrenceandReducer);

  const lastBlockedKeyRef = useRef({ current: null, previous: null });
  const autoUpdateRef = useRef(false);
  const lastCurrentPeriodKeyRef = useRef(null);

  const buildKey = useCallback((p) => `${p?.financialyearid || ''}-${p?.startdate || ''}-${p?.enddate || ''}`, []);
  const isComplete = useCallback((p) => Boolean(p?.financialyearid && p?.startdate && p?.enddate), []);
  const isSamePeriodSameFY = useCallback((a, b) => {
    if (!a || !b) return false;
    return (
      a.financialyearid === b.financialyearid &&
      a.startdate === b.startdate &&
      a.enddate === b.enddate
    );
  }, []);

  const handleChangePrevious = useCallback((val) => {
    setBizviewVariables2((prev) => {
      const next = typeof val === 'function' ? val(prev) : val;
      if (isComplete(bizviewVariables) && isSamePeriodSameFY(next, bizviewVariables)) {
        showWarningSnackbar('Both periods are the same in the selected FY. Please change one to compare.');
        return prev;
      }
      return next;
    });
  }, [bizviewVariables, isComplete, isSamePeriodSameFY]);

  const handleChangeCurrent = useCallback((val) => {
    setBizviewVariables((prev) => {
      const next = typeof val === 'function' ? val(prev) : val;
      if (isComplete(bizviewVariables2) && isSamePeriodSameFY(next, bizviewVariables2)) {
        showWarningSnackbar('Both periods are the same in the selected FY. Please change one to compare.');
        return prev;
      }
      return next;
    });
  }, [bizviewVariables2, isComplete, isSamePeriodSameFY]);

  const validateSelectPrevious = useCallback((candidate) => {
    if (isComplete(bizviewVariables) && isSamePeriodSameFY(candidate, bizviewVariables)) {
      const key = buildKey(candidate);
      if (lastBlockedKeyRef.current.previous !== key) {
        lastBlockedKeyRef.current.previous = key;
        showWarningSnackbar('Both periods are the same in the selected FY. Please change one to compare.');
      }
      return false;
    }
    lastBlockedKeyRef.current.previous = null;
    return true;
  }, [bizviewVariables, isComplete, isSamePeriodSameFY, buildKey]);

  const validateSelectCurrent = useCallback((candidate) => {
    if (isComplete(bizviewVariables2) && isSamePeriodSameFY(candidate, bizviewVariables2)) {
      const key = buildKey(candidate);
      if (lastBlockedKeyRef.current.current !== key) {
        lastBlockedKeyRef.current.current = key;
        showWarningSnackbar('Both periods are the same in the selected FY. Please change one to compare.');
      }
      return false;
    }
    lastBlockedKeyRef.current.current = null;
    return true;
  }, [bizviewVariables2, isComplete, isSamePeriodSameFY, buildKey]);

  useEffect(() => {
    if (autoUpdateRef.current || !areQueryVariableOneValid || !listFYData?.length) {
      return;
    }

    const currentKey = buildKey(bizviewVariables);
    if (lastCurrentPeriodKeyRef.current === currentKey) {
      return;
    }

    const periodInfo = determinePeriodType(bizviewVariables, listFYData);
    if (!periodInfo) {
      lastCurrentPeriodKeyRef.current = currentKey;
      return;
    }

    const prevPeriod = getPreviousYearPeriod(bizviewVariables, listFYData);
    if (!prevPeriod) {
      lastCurrentPeriodKeyRef.current = currentKey;
      return;
    }

    if (isSamePeriodSameFY(prevPeriod, bizviewVariables)) {
      lastCurrentPeriodKeyRef.current = currentKey;
      return;
    }

    autoUpdateRef.current = true;
    lastCurrentPeriodKeyRef.current = currentKey;

    setBizviewVariables2((prev) => {
      if (!isSamePeriodSameFY(prevPeriod, prev)) {
        return prevPeriod;
      }
      return prev;
    });

    setTimeout(() => {
      autoUpdateRef.current = false;
    }, 100);
  }, [bizviewVariables, listFYData, areQueryVariableOneValid, buildKey, isSamePeriodSameFY]);

  useEffect(() => {
    if (areQueryVariableOneValid) {
      setTitles((prev) => ({
        ...prev,
        current: formatPeriodTitle(bizviewVariables, listFYData),
        previewCurrent: formatPeriodTitle(bizviewVariables, listFYData, false),
      }));
    } else {
      setTitles((prev) => ({
        ...prev,
        current: '',
        previewCurrent: '',
      }));
    }

    if (areQueryVariableTwoValid) {
      setTitles((prev) => ({
        ...prev,
        previous: formatPeriodTitle(bizviewVariables2, listFYData),
        previewPrevious: formatPeriodTitle(bizviewVariables2, listFYData, false),
      }));
    } else {
      setTitles((prev) => ({
        ...prev,
        previous: '',
        previewPrevious: '',
      }));
    }
  }, [bizviewVariables, bizviewVariables2, listFYData, areQueryVariableOneValid, areQueryVariableTwoValid]);

  return (
    <>
      <Box sx={{ marginTop: '3px' }}>
        <Box sx={{ marginTop: '2px' }}>
          <Box
            sx={{
              width: '100%',
              borderRadius: 2,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                alignItems: 'center',
                justifyContent: 'flex-end',
                ml: { xs: 0, md: 4 },
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <AIBlinkButton onClick={() => filteredData(filterApiData)} />
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                paddingTop={1}
                spacing={{ xs: 1, md: '2px' }}
                alignItems="center"
                sx={{ width: { xs: '100%', sm: '100%', lg: '50%' } }}
              >
                <Box sx={{ width: '100%' }}>
                  <BiZviewPeriodNew
                    onChange={handleChangePrevious}
                    defaultOffsetMonths={1}
                    preview={titles.previewPrevious}
                    onValidateSelect={validateSelectPrevious}
                    value={areQueryVariableTwoValid ? bizviewVariables2 : null}
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '10px' }}>VS</Typography>
                </Box>

                <Box sx={{ width: '100%' }}>
                  <BiZviewPeriodNew
                    onChange={handleChangeCurrent}
                    preview={titles.previewCurrent}
                    onValidateSelect={validateSelectCurrent}
                  />
                </Box>
              </Stack>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  ml: { xs: 0, md: 4 },
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                <AIBlinkButton
                  onClick={async () => {
                    filteredData(filterApiData);
                  }}
                />
              </Box>
            </Stack>
          </Box>

          <ComparisonCard
            loading={isComparisonCardLoading}
            currentData={areQueryVariableOneValid ? data || [] : []}
            previousData={areQueryVariableTwoValid ? data2 || [] : []}
            currentstockSummary={totalClosingStock1 || []}
            previousStockSummary={totalClosingStock2 || []}
          />
        </Box>
        <Box
          sx={{
            width: '100%',
            p: 0,
            mt: { sm: '8px', xs: 0 },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isUnder1200 ? 'column' : 'row',
              gap: 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 200,
                borderRadius: 2,
                borderColor: 'divider',
                p: 2,
                bgcolor: 'background.paper',
              }}
            >
              <PieChart
                groupData={areQueryVariableTwoValid ? data2 || [] : []}
                title={titles.previous}
                id="chartdiv2"
                isColumnLayout={isUnder1200}
                loading={isPreviousDataLoading}
              />
            </Box>

            <Box
              aria-hidden
              sx={{
                flex: '0 0 auto',
                alignSelf: isUnder1200 ? 'auto' : 'stretch',
                width: isUnder1200 ? '100%' : '2px',
                height: isUnder1200 ? '2px' : 'auto',
                bgcolor: 'divider',
                opacity: 0.6,
                borderRadius: 1,
              }}
            />

            <Box
              sx={{
                flex: 1,
                minHeight: 200,
                borderRadius: 2,
                borderColor: 'divider',
                p: 2,
                bgcolor: 'background.paper',
              }}
            >
             <PieChart
                groupData={areQueryVariableOneValid ? data || [] : []}
                title={titles.current}
                type={'current'}
                id="chartdiv1"
                isColumnLayout={isUnder1200}
                loading={isCurrentDataLoading}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <SimpleModal open={aiModal} setOpen={setAIModal} title={'AI Insights'} body={aisummary} aiModalLoading={aiModalLoading} />
    </>
  );
}

export default memo(BizView);
